import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Transaction, SimulatedTransactionResponse, PublicKey, Connection } from '@solana/web3.js';
import { toast } from 'sonner';
import bs58 from 'bs58';

import { getConnection } from '@/lib/solana/anchorClient';
import * as burnApi from '@/api/burn';
import type { BurnAsset } from '@/api/burn';
import { useAuth } from '@/hooks/useAuth';
import { useWalletAddress } from '@/hooks/useWalletAddress';
import { isMobileWallet } from '@/lib/responsive';
import { parseSolanaError, isUserActionableError } from '@/utils/solanaErrorParser';
import { captureException, createErrorContext } from '@/lib/sentry';

/**
 * Pre-transaction validation: Verify token accounts still exist on-chain
 * This catches stale data from DAS API (accounts that were closed between fetch and burn)
 */
async function validateAssetsBeforeBurn(
  assets: BurnAsset[],
  connection: Connection
): Promise<{ valid: BurnAsset[]; invalid: BurnAsset[] }> {
  // Only validate non-NFT assets with token accounts
  const tokensToValidate = assets.filter(a => !a.isNFT && a.tokenAccount);
  const nftsAndOther = assets.filter(a => a.isNFT || !a.tokenAccount);

  if (tokensToValidate.length === 0) {
    return { valid: assets, invalid: [] };
  }

  console.log(`[BURN-VALIDATE] Pre-burn validation for ${tokensToValidate.length} token accounts...`);

  try {
    const BATCH_SIZE = 100;
    const validTokens: BurnAsset[] = [];
    const invalidTokens: BurnAsset[] = [];

    for (let i = 0; i < tokensToValidate.length; i += BATCH_SIZE) {
      const batch = tokensToValidate.slice(i, i + BATCH_SIZE);
      const pubkeys = batch.map(a => new PublicKey(a.tokenAccount!));
      const accountInfos = await connection.getMultipleAccountsInfo(pubkeys);

      for (let j = 0; j < batch.length; j++) {
        const asset = batch[j];
        const accountInfo = accountInfos[j];

        if (!accountInfo || accountInfo.data.length < 165) {
          console.warn(`[BURN-VALIDATE] Token account no longer exists: ${asset.mint.slice(0, 8)}... (${asset.tokenAccount?.slice(0, 8)}...)`);
          invalidTokens.push(asset);
        } else {
          validTokens.push(asset);
        }
      }
    }

    if (invalidTokens.length > 0) {
      console.warn(`[BURN-VALIDATE] Found ${invalidTokens.length} invalid token accounts (will be skipped)`);
    }
    console.log(`[BURN-VALIDATE] ${validTokens.length} token accounts validated`);

    return { valid: [...validTokens, ...nftsAndOther], invalid: invalidTokens };
  } catch (error) {
    console.error('[BURN-VALIDATE] Validation error:', error);
    // On error, proceed with original assets (backend will catch issues)
    return { valid: assets, invalid: [] };
  }
}

/**
 * Debug utility: Simulate a transaction and log full program logs
 * This helps diagnose "Missing collection" and other MPL Core errors
 */
async function debugSimulateTransaction(
  signedTx: Transaction,
  txLabel: string
): Promise<SimulatedTransactionResponse | null> {
  try {
    const connection = getConnection();
    console.log(`[DEBUG-SIM] Simulating ${txLabel}...`);

    const result = await connection.simulateTransaction(signedTx, undefined, true);

    if (result.value.err) {
      console.error(`[DEBUG-SIM] ${txLabel} simulation FAILED:`);
      console.error(`   Error: ${JSON.stringify(result.value.err)}`);
      console.log(`[DEBUG-SIM] Full program logs:`);
      result.value.logs?.forEach((log, i) => {
        console.log(`   [${i}] ${log}`);
      });

      // Parse MPL Core specific errors
      const mplCoreLogs = result.value.logs?.filter(log =>
        log.includes('CoREENxT6tW1HoK8ypY1SxRMZTcVPm7R94rH4PZNhX7d') ||
        log.includes('mpl_core') ||
        log.includes('Missing collection')
      );
      if (mplCoreLogs && mplCoreLogs.length > 0) {
        console.error(`[DEBUG-SIM] MPL Core specific logs:`);
        mplCoreLogs.forEach(log => console.error(`   ${log}`));
      }
    } else {
      console.log(`[DEBUG-SIM] ${txLabel} simulation succeeded`);
      console.log(`   Units consumed: ${result.value.unitsConsumed}`);
    }

    return result.value;
  } catch (error) {
    console.error(`[DEBUG-SIM] Failed to simulate ${txLabel}:`, error);
    return null;
  }
}

export interface BurnAssetsInput {
  assets: BurnAsset[];
}

export interface BurnAssetsResult {
  signature: string;
  assetsCount: number;
}

// Batch size for burn operations (to avoid transaction size limits)
// Backend uses Legacy Transactions with 1232-byte limit
// 10 tokens ≈ 1266 bytes (slightly over), so use 9 for safety on desktop
// Mobile wallets (Phantom, Backpack) crash with >4 transactions in one popup
// so we use smaller batch size on mobile to reduce memory pressure
const getBurnBatchSize = () => (isMobileWallet() ? 3 : 9);

// Delay between batch requests to avoid backend rate limiting
const BATCH_DELAY_MS = 500;

// Retry configuration for "System busy" errors
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 1000;

/**
 * Sleep utility for adding delays
 */
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Confirmation timeout in milliseconds (60 seconds to handle network congestion)
const CONFIRMATION_TIMEOUT_MS = 60000;

/**
 * Confirm a transaction with timeout and fallback verification
 *
 * Uses blockhash strategy for reliable confirmation, with fallback to
 * getTransaction check if confirmation times out (transaction may have
 * succeeded but confirmation was slow).
 */
async function confirmTransactionWithRetry(
  connection: Connection,
  signature: string,
  blockhash: string,
  lastValidBlockHeight: number
): Promise<void> {
  console.log('[BURN-FLOW] Confirming transaction with blockhash strategy...');

  try {
    // Race between confirmation and timeout
    await Promise.race([
      connection.confirmTransaction(
        {
          signature,
          blockhash,
          lastValidBlockHeight,
        },
        'confirmed'
      ),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('CONFIRMATION_TIMEOUT')), CONFIRMATION_TIMEOUT_MS)
      ),
    ]);

    console.log(`[BURN-FLOW] Transaction confirmed: ${signature}`);
  } catch (confirmError: any) {
    if (confirmError.message === 'CONFIRMATION_TIMEOUT') {
      // Timeout - check if transaction actually exists on-chain
      console.warn('[BURN-FLOW] Confirmation timeout, verifying transaction status...');

      const txInfo = await connection.getTransaction(signature, {
        commitment: 'confirmed',
        maxSupportedTransactionVersion: 0,
      });

      if (txInfo) {
        // Transaction exists
        if (txInfo.meta?.err) {
          throw new Error(`Transaction failed on-chain: ${JSON.stringify(txInfo.meta.err)}`);
        }
        // Transaction succeeded but confirmation was slow
        console.log(`[BURN-FLOW] Transaction verified as confirmed: ${signature}`);
        return;
      }

      // Transaction not found - might still be processing, try one more time
      console.log('[BURN-FLOW] Transaction not found, waiting and retrying verification...');
      await sleep(2000);

      const txInfoRetry = await connection.getTransaction(signature, {
        commitment: 'confirmed',
        maxSupportedTransactionVersion: 0,
      });

      if (txInfoRetry) {
        if (txInfoRetry.meta?.err) {
          throw new Error(`Transaction failed on-chain: ${JSON.stringify(txInfoRetry.meta.err)}`);
        }
        console.log(`[BURN-FLOW] Transaction verified as confirmed (retry): ${signature}`);
        return;
      }

      // Still not found - this is a genuine timeout/failure
      throw new Error(
        'Transaction confirmation timeout. The transaction may still be processing - please check your wallet and refresh the page.'
      );
    }

    // Re-throw other confirmation errors
    throw confirmError;
  }
}

/**
 * Sign transactions with fallback for wallets that don't support signAllTransactions
 */
async function signTransactions(
  walletProvider: any,
  transactions: Transaction[]
): Promise<Transaction[]> {
  // Check if wallet supports signAllTransactions
  if (typeof walletProvider.signAllTransactions === 'function') {
    console.log('[SIGN] Using signAllTransactions for', transactions.length, 'transactions');
    return await walletProvider.signAllTransactions(transactions);
  }

  // Fallback: Sign transactions individually
  console.warn('[SIGN] signAllTransactions not available, falling back to individual signing');

  if (typeof walletProvider.signTransaction !== 'function') {
    throw new Error('Wallet does not support transaction signing');
  }

  const signedTransactions: Transaction[] = [];
  for (let i = 0; i < transactions.length; i++) {
    console.log(`[SIGN] Signing transaction ${i + 1}/${transactions.length}...`);
    const signed = await walletProvider.signTransaction(transactions[i]);
    signedTransactions.push(signed);
  }

  return signedTransactions;
}

/**
 * Burn assets using backend co-signing with batching
 * Flow:
 * 1. Split assets into batches of max 9 assets (legacy transaction limit)
 * 2. Create burn orders on backend for each batch
 * 3. Get pre-signed transactions from backend
 * 4. Sign all transactions at once (single wallet popup)
 * 5. Send signed transactions sequentially to blockchain
 */
async function burnAssets(
  assets: BurnAsset[],
  walletAddress: string,
  walletProvider: any
): Promise<BurnAssetsResult> {
  const flowStartTime = performance.now();
  console.log('[BURN-FLOW] Starting burn for', assets.length, 'assets');

  const errorContext = createErrorContext('burn_assets', walletAddress, {
    assetsCount: assets.length,
    assetTypes: assets.map((a) => a.type),
    assetMints: assets.map((a) => a.mint),
    batchSize: getBurnBatchSize(),
  });

  // Timing data collection
  const timings: {
    totalFlow: number;
    batchSplit: number;
    batches: Array<{
      batchNum: number;
      createOrder: number;
      getSignature: number;
      deserialize: number;
      retries: number;
    }>;
    userSigning: number;
    transactions: Array<{
      txNum: number;
      send: number;
      confirm: number;
    }>;
  } = {
    totalFlow: 0,
    batchSplit: 0,
    batches: [],
    userSigning: 0,
    transactions: [],
  };

  if (assets.length === 0) {
    const err = new Error('No assets to burn');
    captureException(err, errorContext);
    throw err;
  }

  // Pre-burn validation: Filter out assets with closed token accounts
  // This catches stale DAS API data that would cause transaction failures
  const connection = getConnection();
  const { valid: validAssets, invalid: invalidAssets } = await validateAssetsBeforeBurn(assets, connection);

  if (invalidAssets.length > 0) {
    const invalidMints = invalidAssets.map(a => `${a.mint.slice(0, 4)}...${a.mint.slice(-4)}`).slice(0, 5).join(', ');
    const suffix = invalidAssets.length > 5 ? ` and ${invalidAssets.length - 5} more` : '';
    toast.warning(`${invalidAssets.length} token(s) skipped (accounts closed)`, {
      description: `Skipped: ${invalidMints}${suffix}`,
      duration: 5000,
    });
  }

  if (validAssets.length === 0) {
    throw new Error('No valid assets to burn. Token accounts may have been closed.');
  }

  // Use validated assets from here on
  const assetsToProcess = validAssets;

  const isMobile = isMobileWallet();
  const BURN_BATCH_SIZE = getBurnBatchSize();
  console.log(`[BURN-FLOW] Platform: ${isMobile ? 'Mobile' : 'Desktop'}, Batch size: ${BURN_BATCH_SIZE}`);

  // Step 1: Split assets into batches
  const batchSplitStart = performance.now();
  const batches: BurnAsset[][] = [];
  for (let i = 0; i < assetsToProcess.length; i += BURN_BATCH_SIZE) {
    batches.push(assetsToProcess.slice(i, i + BURN_BATCH_SIZE));
  }
  timings.batchSplit = performance.now() - batchSplitStart;
  console.log(`[BURN-FLOW] Split into ${batches.length} batches - took ${timings.batchSplit.toFixed(2)}ms`);

  // connection already declared above for validation
  const signatures: string[] = [];

  // Mobile uses just-in-time signing to prevent blockhash expiration
  // Desktop uses batch signing for better UX (single wallet popup)
  if (isMobile) {
    console.log('[BURN-FLOW] Using mobile just-in-time batch signing flow');
    console.log(`[BURN-FLOW] Will create ${batches.length} batches, get fresh signatures, then sign all together`);

    // MOBILE FLOW: Create all orders → Get all signatures (just-in-time) → Sign all → Send all
    // Key difference from desktop: Smaller batch size (3 vs 9) and just-in-time signature fetching
    const transactions: Transaction[] = [];
    const orderIds: string[] = [];

    // Step 1: Create all orders first
    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      const batchStartTime = performance.now();
      console.log(`[BURN-FLOW] === Mobile Batch ${i + 1}/${batches.length} (${batch.length} assets) ===`);

      const batchTiming = {
        batchNum: i + 1,
        createOrder: 0,
        getSignature: 0,
        deserialize: 0,
        retries: 0,
      };

      // Create order with retry logic
      let orderId: string | undefined;
      let retries = 0;
      const createOrderStart = performance.now();

      while (retries <= MAX_RETRIES) {
        try {
          const orderResponse = await burnApi.createBurnOrder(batch, walletAddress);

          if (orderResponse.code !== 0 || !orderResponse.data) {
            if (orderResponse.message?.includes('System busy') || orderResponse.message?.includes('busy')) {
              throw new Error(orderResponse.message);
            }
            throw new Error(orderResponse.message || 'Failed to create burn order');
          }

          orderId = orderResponse.data;
          batchTiming.createOrder = performance.now() - createOrderStart;
          console.log(`  [BURN-FLOW] Create order API call - took ${batchTiming.createOrder.toFixed(2)}ms`);
          break;
        } catch (error: any) {
          const errorMsg = error?.message || String(error);
          const isSystemBusy = errorMsg.includes('System busy') || errorMsg.includes('busy');

          if (isSystemBusy && retries < MAX_RETRIES) {
            const delayMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, retries);
            console.warn(`  [BURN-FLOW] System busy, retrying order in ${delayMs}ms (attempt ${retries + 1}/${MAX_RETRIES})`);
            await sleep(delayMs);
            retries++;
            batchTiming.retries++;
          } else {
            console.error(`  [BURN-FLOW] Failed to create order for batch ${i + 1}:`, error);
            captureException(error, {
              ...errorContext,
              stage: 'mobile_create_order',
              batchIndex: i + 1,
              batchCount: batches.length,
              batchAssets: batch.map((a) => ({ mint: a.mint, type: a.type })),
              retriesAttempted: retries,
            });
            throw error;
          }
        }
      }

      if (!orderId) {
        const err = new Error(`Failed to create order for batch ${i + 1} after ${MAX_RETRIES} retries`);
        captureException(err, {
          ...errorContext,
          stage: 'mobile_create_order_max_retries',
          batchIndex: i + 1,
          batchCount: batches.length,
          batchAssets: batch.map((a) => ({ mint: a.mint, type: a.type })),
          retriesAttempted: MAX_RETRIES,
        });
        throw err;
      }

      orderIds.push(orderId);
      timings.batches.push(batchTiming);

      const batchTotalTime = performance.now() - batchStartTime;
      console.log(`  [BURN-FLOW] Batch ${i + 1} order created - total: ${batchTotalTime.toFixed(2)}ms`);
    }

    console.log(`[BURN-FLOW] All ${orderIds.length} orders created, now getting fresh signatures...`);

    // Step 2: Get signatures just-in-time (right before user signs)
    // This ensures blockhashes are fresh (<5 seconds old when user signs)
    for (let i = 0; i < orderIds.length; i++) {
      const orderId = orderIds[i];
      const getSignatureStart = performance.now();

      let retries = 0;
      let transaction: Transaction | undefined;

      while (retries <= MAX_RETRIES) {
        try {
          const signatureResponse = await burnApi.generateBurnSignature(orderId);

          if (signatureResponse.code !== 0 || !signatureResponse.data) {
            throw new Error(signatureResponse.message || 'Failed to get signature');
          }

          const getSignatureTime = performance.now() - getSignatureStart;
          console.log(`  [BURN-FLOW] Got fresh signature for batch ${i + 1} - took ${getSignatureTime.toFixed(2)}ms`);

          const { serialized_tx } = signatureResponse.data;
          const txBuffer = Buffer.from(serialized_tx, 'hex');
          transaction = Transaction.from(txBuffer);
          console.log(`  [BURN-FLOW] Transaction ${i + 1} deserialized`);
          break;
        } catch (error: any) {
          const errorMsg = error?.message || String(error);
          const isRetryable = errorMsg.includes('500') || errorMsg.includes('Internal server error') || errorMsg.includes('busy');

          if (isRetryable && retries < MAX_RETRIES) {
            const delayMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, retries);
            console.warn(`  [BURN-FLOW] Signature error, retrying in ${delayMs}ms (attempt ${retries + 1}/${MAX_RETRIES})`);
            await sleep(delayMs);
            retries++;
          } else {
            console.error(`  [BURN-FLOW] Failed to get signature for batch ${i + 1}:`, error);
            captureException(error, {
              ...errorContext,
              stage: 'mobile_get_signature',
              batchIndex: i + 1,
              batchCount: orderIds.length,
              orderId,
              retriesAttempted: retries,
            });
            throw error;
          }
        }
      }

      if (!transaction) {
        const err = new Error(`Failed to get signature for batch ${i + 1} after ${MAX_RETRIES} retries`);
        captureException(err, {
          ...errorContext,
          stage: 'mobile_get_signature_max_retries',
          batchIndex: i + 1,
          batchCount: orderIds.length,
          orderId,
          retriesAttempted: MAX_RETRIES,
        });
        throw err;
      }

      transactions.push(transaction);
    }

    console.log(`[BURN-FLOW] All ${transactions.length} transactions ready with fresh blockhashes`);

    // Store original signatures before signing (for wallets that corrupt them like Gate)
    const originalSignaturesMobileBurn: { pubkey: string; signature: Buffer | null }[][] = [];
    for (const tx of transactions) {
      const sigs: { pubkey: string; signature: Buffer | null }[] = [];
      for (const sig of tx.signatures) {
        if (sig.signature) {
          sigs.push({ pubkey: sig.publicKey.toBase58(), signature: Buffer.from(sig.signature) });
        }
      }
      originalSignaturesMobileBurn.push(sigs);
    }

    // Step 3: Sign all transactions at once (single wallet popup with 3 transactions)
    console.log(`[BURN-FLOW] Requesting user to sign all ${transactions.length} transactions...`);
    const userSigningStart = performance.now();
    let signedTransactions: Transaction[];
    try {
      signedTransactions = await signTransactions(walletProvider, transactions);
    } catch (error: any) {
      console.error(`  [BURN-FLOW] User signing failed:`, error);
      captureException(error, {
        ...errorContext,
        stage: 'mobile_user_signing',
        transactionCount: transactions.length,
        orderIds,
      });
      throw error;
    }
    timings.userSigning = performance.now() - userSigningStart;
    console.log(`[BURN-FLOW] User signed all ${transactions.length} transactions - took ${timings.userSigning.toFixed(2)}ms`);

    // Restore any missing or corrupted signatures (Gate Wallet via AppKit may overwrite backend signatures)
    for (let i = 0; i < signedTransactions.length; i++) {
      const signedTx = signedTransactions[i];
      const origSigs = originalSignaturesMobileBurn[i];

      for (const origSig of origSigs) {
        const existingSig = signedTx.signatures.find(
          (s: { publicKey: { toBase58: () => string }; signature: Buffer | null }) => s.publicKey.toBase58() === origSig.pubkey
        );

        if (existingSig && origSig.signature) {
          const currentSigHex = existingSig.signature ? Buffer.from(existingSig.signature).toString('hex') : null;
          const originalSigHex = origSig.signature.toString('hex');

          if (!existingSig.signature || currentSigHex !== originalSigHex) {
            console.log(`[BURN-FLOW:Mobile] Restoring signature for ${origSig.pubkey.slice(0, 8)}... (was ${currentSigHex ? 'corrupted' : 'missing'})`);
            existingSig.signature = origSig.signature;
          }
        }
      }
    }

    // Step 4: Send each signed transaction
    for (let i = 0; i < signedTransactions.length; i++) {
      const signedTx = signedTransactions[i];
      console.log(`[BURN-FLOW] === Transaction ${i + 1}/${signedTransactions.length} ===`);

      const txTiming = {
        txNum: i + 1,
        send: 0,
        confirm: 0,
      };

      try {
        // Debug: Simulate first to get full program logs on failure
        await debugSimulateTransaction(signedTx, `Mobile TX ${i + 1}/${signedTransactions.length}`);

        // Get fresh blockhash for confirmation tracking
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

        const sendStart = performance.now();
        const signature = await connection.sendRawTransaction(signedTx.serialize(), {
          skipPreflight: false,
          preflightCommitment: 'confirmed',
        });
        txTiming.send = performance.now() - sendStart;
        console.log(`  [BURN-FLOW] Transaction sent - took ${txTiming.send.toFixed(2)}ms - ${signature}`);

        const confirmStart = performance.now();
        await confirmTransactionWithRetry(connection, signature, blockhash, lastValidBlockHeight);
        txTiming.confirm = performance.now() - confirmStart;
        console.log(`  [BURN-FLOW] Transaction confirmed - took ${txTiming.confirm.toFixed(2)}ms`);

        signatures.push(signature);
        timings.transactions.push(txTiming);
      } catch (error: any) {
        const errorMessage = error?.message || String(error);
        const isAlreadyProcessed = errorMessage.includes('already been processed') ||
                                   errorMessage.includes('This transaction has already been processed') ||
                                   errorMessage.includes('already processed') ||
                                   errorMessage.toLowerCase().includes('transaction has already been processed');

        if (isAlreadyProcessed) {
          console.log(`  [BURN-FLOW] Transaction was already processed, checking status...`);
          const signature = bs58.encode(signedTx.signature!);

          try {
            // Verify the transaction status on-chain
            const txInfo = await connection.getTransaction(signature, {
              commitment: 'confirmed',
              maxSupportedTransactionVersion: 0,
            });

            if (txInfo && !txInfo.meta?.err) {
              console.log(`  [BURN-FLOW] Transaction already processed successfully`);
              signatures.push(signature);
              timings.transactions.push(txTiming);
              continue;
            }
          } catch (statusError) {
            console.warn(`  [BURN-FLOW] Could not verify transaction status:`, statusError);
          }
        }

        // Debug: Run simulation again to capture logs on failure
        console.error(`  [BURN-FLOW] Transaction failed, running debug simulation...`);
        const simResult = await debugSimulateTransaction(signedTx, `Failed Mobile TX ${i + 1}`);

        console.error(`  [BURN-FLOW] Transaction failed:`, error);

        captureException(error, {
          ...errorContext,
          stage: 'mobile_send_transaction',
          transactionIndex: i + 1,
          transactionCount: signedTransactions.length,
          completedSignatures: signatures,
          simulationError: simResult?.err ? JSON.stringify(simResult.err) : null,
          simulationLogs: simResult?.logs?.slice(-20), // Last 20 logs to avoid huge payloads
          unitsConsumed: simResult?.unitsConsumed,
        });
        throw error;
      }
    }

    console.log(`[BURN-FLOW] All ${signedTransactions.length} mobile transactions complete`);
  } else {
    // DESKTOP FLOW: Create all orders → Get all signatures → Sign all → Send all
    console.log('[BURN-FLOW] Using desktop batch signing flow');
    console.log('[BURN-FLOW] Creating burn orders and requesting signatures');
    const transactions: Transaction[] = [];

    for (let i = 0; i < batches.length; i++) {
    const batch = batches[i];
    const batchStartTime = performance.now();
    console.log(`[BURN-FLOW] === Batch ${i + 1}/${batches.length} (${batch.length} assets) ===`);

    const batchTiming = {
      batchNum: i + 1,
      createOrder: 0,
      getSignature: 0,
      deserialize: 0,
      retries: 0,
    };

    // Step 2a: Create order with retry logic
    let orderId: string | undefined;
    let retries = 0;
    const createOrderStart = performance.now();

    while (retries <= MAX_RETRIES) {
      try {
        const orderResponse = await burnApi.createBurnOrder(batch, walletAddress);

        if (orderResponse.code !== 0 || !orderResponse.data) {
          // Check if this is a "System busy" error
          if (orderResponse.message?.includes('System busy') || orderResponse.message?.includes('busy')) {
            throw new Error(orderResponse.message);
          }
          // Other errors should fail immediately
          throw new Error(orderResponse.message || 'Failed to create burn order');
        }

        // Success!
        orderId = orderResponse.data;
        batchTiming.createOrder = performance.now() - createOrderStart;
        console.log(`  [BURN-FLOW] Create order API call - took ${batchTiming.createOrder.toFixed(2)}ms`);
        break;
      } catch (error: any) {
        const errorMsg = error?.message || String(error);
        const isSystemBusy = errorMsg.includes('System busy') || errorMsg.includes('busy');

        if (isSystemBusy && retries < MAX_RETRIES) {
          // Retry with exponential backoff
          const delayMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, retries);
          console.warn(`  [BURN-FLOW] System busy, retrying order in ${delayMs}ms (attempt ${retries + 1}/${MAX_RETRIES})`);
          await sleep(delayMs);
          retries++;
          batchTiming.retries++;
        } else {
          // Max retries reached or non-retryable error
          console.error(`  [BURN-FLOW] Failed to create order for batch ${i + 1}:`, error);
          // Report to Sentry with full context before throwing
          captureException(error, {
            ...errorContext,
            stage: 'desktop_create_order',
            batchIndex: i + 1,
            batchCount: batches.length,
            batchAssets: batch.map((a) => ({ mint: a.mint, type: a.type })),
            retriesAttempted: retries,
          });
          throw error;
        }
      }
    }

    if (!orderId) {
      const err = new Error(`Failed to create order for batch ${i + 1} after ${MAX_RETRIES} retries`);
      captureException(err, {
        ...errorContext,
        stage: 'desktop_create_order_max_retries',
        batchIndex: i + 1,
        batchCount: batches.length,
        batchAssets: batch.map((a) => ({ mint: a.mint, type: a.type })),
        retriesAttempted: MAX_RETRIES,
      });
      throw err;
    }

    // Step 2b: Immediately get signature for this order (with retry logic)
    retries = 0;
    let transaction: Transaction | undefined;
    const getSignatureStart = performance.now();

    while (retries <= MAX_RETRIES) {
      try {
        const signatureResponse = await burnApi.generateBurnSignature(orderId);

        if (signatureResponse.code !== 0 || !signatureResponse.data) {
          throw new Error(signatureResponse.message || 'Failed to get signature');
        }

        batchTiming.getSignature = performance.now() - getSignatureStart;
        console.log(`  [BURN-FLOW] Get signature API call - took ${batchTiming.getSignature.toFixed(2)}ms`);

        const deserializeStart = performance.now();
        const { serialized_tx } = signatureResponse.data;
        const txBuffer = Buffer.from(serialized_tx, 'hex');
        transaction = Transaction.from(txBuffer);
        batchTiming.deserialize = performance.now() - deserializeStart;
        console.log(`  [BURN-FLOW] Transaction deserialized - took ${batchTiming.deserialize.toFixed(2)}ms`);
        break;
      } catch (error: any) {
        const errorMsg = error?.message || String(error);
        const isRetryable = errorMsg.includes('500') || errorMsg.includes('Internal server error') || errorMsg.includes('busy');

        if (isRetryable && retries < MAX_RETRIES) {
          // Retry with exponential backoff
          const delayMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, retries);
          console.warn(`  [BURN-FLOW] Signature error, retrying in ${delayMs}ms (attempt ${retries + 1}/${MAX_RETRIES})`);
          await sleep(delayMs);
          retries++;
          batchTiming.retries++;
        } else {
          // Max retries reached or non-retryable error
          console.error(`  [BURN-FLOW] Failed to get signature for batch ${i + 1}:`, error);
          captureException(error, {
            ...errorContext,
            stage: 'desktop_get_signature',
            batchIndex: i + 1,
            batchCount: batches.length,
            orderId,
            retriesAttempted: retries,
          });
          throw error;
        }
      }
    }

    if (!transaction) {
      const err = new Error(`Failed to get signature for batch ${i + 1} after ${MAX_RETRIES} retries`);
      captureException(err, {
        ...errorContext,
        stage: 'desktop_get_signature_max_retries',
        batchIndex: i + 1,
        batchCount: batches.length,
        orderId,
        retriesAttempted: MAX_RETRIES,
      });
      throw err;
    }

    transactions.push(transaction);
    timings.batches.push(batchTiming);

    const batchTotalTime = performance.now() - batchStartTime;
    console.log(`  [BURN-FLOW] Batch ${i + 1} complete - total: ${batchTotalTime.toFixed(2)}ms`);

    // Add delay before next batch (except for last batch)
    if (i < batches.length - 1) {
      console.log(`  [BURN-FLOW] Waiting ${BATCH_DELAY_MS}ms before next batch...`);
      await sleep(BATCH_DELAY_MS);
    }
  }

    console.log('[BURN-FLOW] All', transactions.length, 'transactions ready');

    // Store original signatures before signing (for wallets that corrupt them like Gate)
    const originalSignaturesBurn: { pubkey: string; signature: Buffer | null }[][] = [];
    for (const tx of transactions) {
      const sigs: { pubkey: string; signature: Buffer | null }[] = [];
      for (const sig of tx.signatures) {
        if (sig.signature) {
          sigs.push({ pubkey: sig.publicKey.toBase58(), signature: Buffer.from(sig.signature) });
        }
      }
      originalSignaturesBurn.push(sigs);
    }

    // Step 3: Sign all transactions at once (single wallet popup)
    console.log('[BURN-FLOW] Requesting user to sign all', transactions.length, 'transactions...');
    const userSigningStart = performance.now();
    let signedTransactions: Transaction[];
    try {
      signedTransactions = await signTransactions(walletProvider, transactions);
    } catch (error: any) {
      console.error(`  [BURN-FLOW] User signing failed:`, error);
      captureException(error, {
        ...errorContext,
        stage: 'desktop_user_signing',
        transactionCount: transactions.length,
        batchCount: batches.length,
      });
      throw error;
    }
    timings.userSigning = performance.now() - userSigningStart;
    console.log(`[BURN-FLOW] User signed all transactions - took ${timings.userSigning.toFixed(2)}ms`);

    // Restore any missing or corrupted signatures (Gate Wallet via AppKit may overwrite backend signatures)
    for (let i = 0; i < signedTransactions.length; i++) {
      const signedTx = signedTransactions[i];
      const origSigs = originalSignaturesBurn[i];

      for (const origSig of origSigs) {
        const existingSig = signedTx.signatures.find(
          (s: { publicKey: { toBase58: () => string }; signature: Buffer | null }) => s.publicKey.toBase58() === origSig.pubkey
        );

        if (existingSig && origSig.signature) {
          const currentSigHex = existingSig.signature ? Buffer.from(existingSig.signature).toString('hex') : null;
          const originalSigHex = origSig.signature.toString('hex');

          if (!existingSig.signature || currentSigHex !== originalSigHex) {
            console.log(`[BURN-FLOW] Restoring signature for ${origSig.pubkey.slice(0, 8)}... (was ${currentSigHex ? 'corrupted' : 'missing'})`);
            existingSig.signature = origSig.signature;
          }
        }
      }
    }

    // Step 4: Send each signed transaction to blockchain
    for (let i = 0; i < signedTransactions.length; i++) {
    const signedTx = signedTransactions[i];
    console.log(`[BURN-FLOW] === Transaction ${i + 1}/${signedTransactions.length} ===`);

    const txTiming = {
      txNum: i + 1,
      send: 0,
      confirm: 0,
    };

    try {
      // Debug: Simulate first to get full program logs on failure
      await debugSimulateTransaction(signedTx, `Desktop TX ${i + 1}/${signedTransactions.length}`);

      // Get fresh blockhash for confirmation tracking
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

      const sendStart = performance.now();
      const signature = await connection.sendRawTransaction(signedTx.serialize(), {
        skipPreflight: false,
        preflightCommitment: 'confirmed',
      });
      txTiming.send = performance.now() - sendStart;
      console.log(`  [BURN-FLOW] Transaction sent - took ${txTiming.send.toFixed(2)}ms - ${signature}`);

      // Wait for confirmation with robust timeout and fallback
      const confirmStart = performance.now();
      await confirmTransactionWithRetry(connection, signature, blockhash, lastValidBlockHeight);
      txTiming.confirm = performance.now() - confirmStart;
      console.log(`  [BURN-FLOW] Transaction confirmed - took ${txTiming.confirm.toFixed(2)}ms`);

      signatures.push(signature);
      timings.transactions.push(txTiming);
    } catch (error: any) {
      // Check if this is a "transaction already processed" error
      const errorMessage = error?.message || String(error);
      const isAlreadyProcessed = errorMessage.includes('already been processed') ||
                                 errorMessage.includes('This transaction has already been processed') ||
                                 errorMessage.includes('already processed') ||
                                 errorMessage.toLowerCase().includes('transaction has already been processed');

      if (isAlreadyProcessed) {
        console.log(`  [BURN-FLOW] Transaction was already processed, checking status...`);

        // Get signature from signed transaction
        const signature = bs58.encode(signedTx.signature!);

        try {
          // Check if transaction actually succeeded using getTransaction for more reliable info
          const txInfo = await connection.getTransaction(signature, {
            commitment: 'confirmed',
            maxSupportedTransactionVersion: 0,
          });

          if (txInfo && !txInfo.meta?.err) {
            // Transaction succeeded!
            console.log(`  [BURN-FLOW] Transaction already processed successfully`);
            signatures.push(signature);
            timings.transactions.push(txTiming);
            continue; // Move to next transaction
          }
        } catch (statusError) {
          console.warn(`  [BURN-FLOW] Could not verify transaction status:`, statusError);
        }
      }

      // Debug: Run simulation again to capture logs on failure
      console.error(`  [BURN-FLOW] Transaction failed, running debug simulation...`);
      const simResult = await debugSimulateTransaction(signedTx, `Failed Desktop TX ${i + 1}`);

      // Transaction failed
      console.error(`  [BURN-FLOW] Transaction failed:`, error);

      // Report to Sentry with full context including simulation logs
      captureException(error, {
        ...errorContext,
        stage: 'desktop_send_transaction',
        transactionIndex: i + 1,
        transactionCount: signedTransactions.length,
        completedSignatures: signatures,
        simulationError: simResult?.err ? JSON.stringify(simResult.err) : null,
        simulationLogs: simResult?.logs?.slice(-20), // Last 20 logs to avoid huge payloads
        unitsConsumed: simResult?.unitsConsumed,
      });
      throw error;
    }
    }
  }

  timings.totalFlow = performance.now() - flowStartTime;

  // ============================================================================
  // PERFORMANCE SUMMARY
  // ============================================================================
  console.log('\n[BURN-FLOW] ========== PERFORMANCE SUMMARY ==========');
  console.log(`[BURN-FLOW] Total Flow Time: ${timings.totalFlow.toFixed(2)}ms`);
  console.log(`[BURN-FLOW] Batch Split: ${timings.batchSplit.toFixed(2)}ms (${((timings.batchSplit / timings.totalFlow) * 100).toFixed(1)}%)`);
  console.log(`[BURN-FLOW] User Signing: ${timings.userSigning.toFixed(2)}ms (${((timings.userSigning / timings.totalFlow) * 100).toFixed(1)}%)`);

  // Batch timings breakdown
  const totalCreateOrder = timings.batches.reduce((sum, b) => sum + b.createOrder, 0);
  const totalGetSignature = timings.batches.reduce((sum, b) => sum + b.getSignature, 0);
  const totalDeserialize = timings.batches.reduce((sum, b) => sum + b.deserialize, 0);
  const totalRetries = timings.batches.reduce((sum, b) => sum + b.retries, 0);

  console.log(`\n[BURN-FLOW] Batch Operations (${timings.batches.length} batches):`);
  console.log(`  - Total Create Order: ${totalCreateOrder.toFixed(2)}ms (${((totalCreateOrder / timings.totalFlow) * 100).toFixed(1)}%)`);
  console.log(`  - Total Get Signature: ${totalGetSignature.toFixed(2)}ms (${((totalGetSignature / timings.totalFlow) * 100).toFixed(1)}%)`);
  console.log(`  - Total Deserialize: ${totalDeserialize.toFixed(2)}ms (${((totalDeserialize / timings.totalFlow) * 100).toFixed(1)}%)`);
  console.log(`  - Total Retries: ${totalRetries}`);

  // Transaction timings breakdown
  const totalSend = timings.transactions.reduce((sum, t) => sum + t.send, 0);
  const totalConfirm = timings.transactions.reduce((sum, t) => sum + t.confirm, 0);

  console.log(`\n[BURN-FLOW] Transaction Operations (${timings.transactions.length} transactions):`);
  console.log(`  - Total Send: ${totalSend.toFixed(2)}ms (${((totalSend / timings.totalFlow) * 100).toFixed(1)}%)`);
  console.log(`  - Total Confirm: ${totalConfirm.toFixed(2)}ms (${((totalConfirm / timings.totalFlow) * 100).toFixed(1)}%)`);

  // Per-batch detail table
  console.log('\n[BURN-FLOW] Per-Batch Breakdown:');
  console.table(
    timings.batches.map((b) => ({
      'Batch': `${b.batchNum}/${timings.batches.length}`,
      'Create Order (ms)': b.createOrder.toFixed(2),
      'Get Signature (ms)': b.getSignature.toFixed(2),
      'Deserialize (ms)': b.deserialize.toFixed(2),
      'Retries': b.retries,
      'Total (ms)': (b.createOrder + b.getSignature + b.deserialize).toFixed(2),
    }))
  );

  // Per-transaction detail table
  console.log('\n[BURN-FLOW] Per-Transaction Breakdown:');
  console.table(
    timings.transactions.map((t) => ({
      'Transaction': `${t.txNum}/${timings.transactions.length}`,
      'Send (ms)': t.send.toFixed(2),
      'Confirm (ms)': t.confirm.toFixed(2),
      'Total (ms)': (t.send + t.confirm).toFixed(2),
    }))
  );

  // Identify bottleneck
  const bottlenecks = [
    { name: 'Create Order', time: totalCreateOrder },
    { name: 'Get Signature', time: totalGetSignature },
    { name: 'User Signing', time: timings.userSigning },
    { name: 'Send Transactions', time: totalSend },
    { name: 'Confirm Transactions', time: totalConfirm },
  ].sort((a, b) => b.time - a.time);

  console.log('\n[BURN-FLOW] Bottleneck Analysis (sorted by time):');
  bottlenecks.forEach((item, idx) => {
    const percentage = ((item.time / timings.totalFlow) * 100).toFixed(1);
    const emoji = idx === 0 ? '[SLOW]' : idx === 1 ? '[MED]' : '[OK]';
    console.log(`  ${emoji} ${item.name}: ${item.time.toFixed(2)}ms (${percentage}%)`);
  });

  console.log('[BURN-FLOW] =======================================\n');

  return {
    signature: signatures[0], // Return first signature for compatibility
    assetsCount: assetsToProcess.length,
  };
}

export function useBurnAssets() {
  const { address, walletProvider } = useWalletAddress();
  const { isAuthenticated, authenticate } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: BurnAssetsInput) => {
      if (!address) {
        throw new Error('Wallet not connected');
      }
      if (!walletProvider) {
        throw new Error('Wallet provider not available');
      }

      // Check authentication before proceeding
      if (!isAuthenticated) {
        console.log('[useBurnAssets] User not authenticated, prompting authentication...');
        const authenticated = await authenticate();
        if (!authenticated) {
          throw new Error('Authentication required. Please sign the message to verify your wallet ownership.');
        }
      }

      return burnAssets(input.assets, address, walletProvider);
    },
    onSuccess: (data) => {
      console.log('[useBurnAssets] Burn successful:', data);

      // Invalidate user progress to refresh balance
      queryClient.invalidateQueries({ queryKey: ['userProgress', address] });

      // Invalidate history to show new burn transaction
      queryClient.invalidateQueries({ queryKey: ['solHistory', address] });

      // Invalidate Gate campaign to show updated bonus points
      queryClient.invalidateQueries({ queryKey: ['gateCampaign'] });
    },
    onError: (error: Error, variables: BurnAssetsInput) => {
      console.error('[useBurnAssets] Burn failed:', error);

      // Parse the error to provide user-friendly messages
      const parsedError = parseSolanaError(error);

      captureException(error, {
        operation: 'burn_assets_fallback',
        assetsCount: variables.assets.length,
        assetMints: variables.assets.map((a) => a.mint),
        parsedErrorType: parsedError.type,
        parsedErrorMessage: parsedError.message,
      });

      // For actionable errors (like insufficient SOL), show helpful guidance
      if (isUserActionableError(parsedError)) {
        const description = parsedError.suggestedAction
          ? `${parsedError.message}\n\n${parsedError.suggestedAction}`
          : parsedError.message;

        toast.error('Transaction Failed', {
          description,
          duration: 8000, // Show longer for actionable errors
        });
      } else {
        // For other errors, show generic message
        toast.error('Failed to burn assets', {
          description: parsedError.message,
        });
      }
    },
  });
}
